<template>
  <ion-item ref="checkboxRef" mode="md" lines="none" :class="[isEdit ? 'edit-mode' : '', size]">
    <ion-input v-if="isEdit" v-model="data.text" autofocus />
    <ion-checkbox
      v-else
      mode="md"
      justify="start"
      :checked="checked"
      label-placement="end"
      @ion-change="$emit('onChecked', handleCheckboxChange($event))"
    >
      <div :class="['ion-text-wrap', checked ? 'td-lt' : '', 'text']">{{ data.text }}</div>
    </ion-checkbox>

    <ion-buttons v-if="!isEdit && (isAnyMobile ? true : isHovered)" slot="end">
      <ion-button :disabled="isLoading" @click="setIsEdit(true)">
        <icons-provider
          slot="icon-only"
          :icon-props="{
            width: '14',
            height: '14',
            fill: 'var(--ion-color-medium)',
          }"
          :name="AppIconsEnum.PencilSquare"
        />
      </ion-button>
    </ion-buttons>
    <div v-else-if="!isEdit" slot="end" class="placeholder" />
  </ion-item>

  <div v-if="textErrors.length > 0">
    <form-error name="textErrors" :errors-array="textErrors" />
  </div>

  <div v-if="isEdit" class="controls flex gap-md mt-md mb-lg jc-between">
    <div class="flex gap-md">
      <ion-button :disabled="isLoading" mode="md" fill="solid" color="success" @click.stop="saveText()">
        {{ $t('save') }}
      </ion-button>
      <ion-button :disabled="isLoading" mode="md" fill="solid" class="cancel-btn" @click.stop="cancel()">
        {{ $t('cancel') }}
      </ion-button>
    </div>
    <div class="">
      <ion-button :disabled="isLoading" mode="md" fill="solid" class="cancel-btn" @click.stop="deleteCheckbox()">
        {{ $t('appPopoverMenu.delete.title') }}
      </ion-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { IonItem, IonCheckbox, IonButtons, IonButton, IonInput } from '@ionic/vue';
import { computed, ComputedRef, reactive, ref, useTemplateRef, type PropType } from 'vue';
import { useElementHover } from '@vueuse/core';
import { IconsProvider, FormError } from '@/components';

import { AppIconsEnum } from '@/enums';
import { isAnyMobile } from '@/helpers';

import { required } from '@/helpers';
import useVuelidate, { ErrorObject } from '@vuelidate/core';
import { watch } from 'vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  text: {
    type: String,
    required: true,
  },
  checked: {
    type: Boolean,
    required: true,
  },
  size: {
    type: String as PropType<'small' | 'default'>,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  arrayOfEditable: {
    type: Array as PropType<number[]>,
    required: true,
  },
});

const emit = defineEmits(['onChecked', 'onUpdateText', 'onDelete', 'update:arrayOfEditable']);

//#region Variables
const rules = computed(() => ({
  text: {
    required,
  },
}));
const checkboxRef = useTemplateRef<HTMLButtonElement>('checkboxRef');
const isHovered = useElementHover(checkboxRef);
const isEdit = computed(() => props.arrayOfEditable.includes(props.id));
const originalText = ref(props.text);

const data = reactive({
  text: props.text,
});

const v$ = useVuelidate(rules, data);
const textErrors: ComputedRef<string[]> = computed(() => {
  const errors = v$.value.text.$errors.map((m: ErrorObject) => m.$message as string);
  return [...errors];
});
//#endregion

//#region Methods
function handleCheckboxChange(event: Event) {
  return (event.target as HTMLIonCheckboxElement).checked;
}

function setIsEdit(active: boolean) {
  emit(
    'update:arrayOfEditable',
    active ? [...props.arrayOfEditable, props.id] : props.arrayOfEditable.filter((n) => n !== props.id)
  );
}

async function saveText() {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  setIsEdit(false);
  emit('onUpdateText', data.text);
}

function deleteCheckbox() {
  setIsEdit(false);
  emit('onDelete');
}

function cancel() {
  data.text = originalText.value;
  setIsEdit(false);
}
//#endregion

//#region Watchers
watch(
  () => props.text,
  (newVal) => {
    data.text = newVal;
    originalText.value = newVal;
  },
  { immediate: true }
);

watch(
  () => isEdit.value,
  (editing) => {
    if (editing) originalText.value = data.text;
  }
);
//#endregion
</script>

<style scoped lang="scss">
ion-item {
  margin-bottom: 0;
  --min-height: 44px;
  --border-radius: #{app-radius(md)};
  --inner-padding-start: 0;
  --padding-start: 0;
  --background: transparent;
  --background-hover: transparent;
  --background-activated: transparent;
  --background-focused: transparent;
  --ripple-color: transparent;
  --inner-padding-end: 0;

  ion-checkbox {
    margin-left: 0;
    --checkmark-color: var(--ion-color-light);
    --checkbox-background-checked: var(--ion-color-success);
    --border-color-checked: var(--ion-color-success);
  }

  ion-input {
    --color: var(--ion-color-dark);
    --placeholder-color: var(--ion-color-dark);
    caret-color: auto;
    --background: transparent;
  }
  &.edit-mode {
    --background: var(--ion-color-light-custom);
    --border-radius: #{app-radius(md)};
    --inner-padding-start: #{app-padding(md)};
    --inner-padding-end: #{app-padding(md)};
  }
  &.small {
    --min-height: 44px;
    ion-buttons ion-button {
      width: 44px !important;
      height: 44px !important;
    }
  }

  .text {
    color: var(--ion-color-dark);
  }

  .placeholder {
    width: 44px;
  }
}

.controls {
  ion-button {
    @include resetStyleFromIonicButton;
    --border-radius: #{app-radius(md)};
    &.cancel-btn {
      --background: var(--ion-color-custom-element-lighter);
      --color: var(--ion-color-medium);
    }
  }
}
</style>
